import { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../../../auth';
import ImageDrop from '../../ImageDrop';
import { useDragReorder } from '../../useDragReorder';
import { SectionBuilder } from '../../components/SectionBuilder';
import { getInitialContent } from '../../data/wrInitialContent';
import { useSection } from '../../useSection';

const API = '/api/prodcat';
const SLUG = 'military';

let _uid = 0;
const uid = () => `u${++_uid}`;

const toSlug  = (model) => (model || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const newLink  = () => ({ _id: uid(), key: '', url: '' });
const newSpecSheet = (label = '새 탭') => ({ _id: uid(), label, content: '' });
const newItem  = () => ({ _id: uid(), model: '', category: '', imageKeys: [], features: '', tags: [], specSheets: [
  newSpecSheet('Technical Profile'), newSpecSheet('Specifications'), newSpecSheet('Order Information'),
], links: [] });
const newWrItem = () => ({ _id: uid(), _wr: true, model: '', id: '', badge: '', tagline: '', heroImg: '', sectionContent: '', bnContent: '', cert: '', wrUrl: '' });
const newSubcat = () => ({ _id: uid(), subcat: '새 소분류', items: [newItem()] });
const newWrSubcat = (name = '새 제품') => ({ _id: uid(), subcat: name, items: [newWrItem()] });
const newCat   = (vendor = '', editorType = 'form') => ({ _id: uid(), intNum: null, vendor, editorType, category: '새 중분류', subcats: [newSubcat()] });
const newWrCat = (vendor = 'Wind River', category = '새 중분류') => ({ _id: uid(), intNum: null, vendor, editorType: 'blocknote', category, subcats: [newWrSubcat()] });

/* Wind River 기본 제품 데이터 */
const WR_DEFAULTS = {
  Platforms: [
    { id:'vxworks', name:'VxWorks', badge:'RTOS', tagline:'세계 1위 실시간 운영체제 — 미션 크리티컬 시스템의 표준', desc:"Build high-performance, safety-certifiable embedded systems software, where 99.9999% reliability won't do.", heroImg:'https://www.windriver.com/sites/default/files/2025-10/2040x800%20vxworks.jpg', caps:[{title:'Hard Real-Time Determinism',desc:'Single-nanosecond determinism for time-critical applications'},{title:'Safety Certification',desc:'DO-178C · IEC 61508 · ISO 26262 — 600+ programs certified'},{title:'Edge AI-Ready',desc:'Rapidly implement AI/ML applications at the intelligent edge'},{title:'Full TSN Stack',desc:'Achieve deterministic networking with a full TSN software stack'}], cert:'DO-178C / IEC 61508 / ISO 26262 / FACE™', wrUrl:'/products/embedded/vxworks' },
    { id:'elxr-pro', name:'eLxr Pro', badge:'Enterprise Linux', tagline:'클라우드-엣지 배포를 위한 엔터프라이즈 Linux', desc:'Deliver mission-critical and AI/ML workloads with enterprise-grade Linux built for the performance, safety, and compliance needs of regulated industries.', heroImg:'https://www.windriver.com/sites/default/files/2025-12/2040x800%20eLrx%20Pro.png', caps:[{title:'Scalable Edge-to-Cloud Ops',desc:'Scalable deployments with lower operational costs'},{title:'Open & Portable',desc:'Enterprise Linux extending ecosystem and lifecycle'},{title:'Enterprise High-Performance',desc:'Predictable, low-latency performance for edge AI'},{title:'Secure Architecture',desc:'Built-in security and compliance updates'}], cert:'', wrUrl:'/products/enterprise/elxr-pro' },
    { id:'cloud-platform', name:'Wind River Cloud Platform', badge:'Cloud Infrastructure', tagline:'미션 크리티컬 워크로드를 위한 프라이빗 클라우드', desc:'Gain agility, control, and resilience — powered by an intelligent platform that unifies and automates mission-critical applications in any environment.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-cloud-platform-hero.jpg', caps:[{title:'Intelligent Orchestration',desc:'AI-driven automation for complex distributed systems'},{title:'Lifecycle Management',desc:'Simplified operations from deployment to end-of-life'},{title:'Open Standards',desc:'Kubernetes-native with open APIs for maximum flexibility'},{title:'Mission-Critical Reliability',desc:'Five-nines availability for carrier and defense workloads'}], cert:'', wrUrl:'/products/cloud/wind-river-cloud-platform' },
    { id:'helix', name:'Helix Virtualization Platform', badge:'Type 1 Hypervisor', tagline:'안전 인증 혼합-임계도 시스템을 위한 하이퍼바이저', desc:'Consolidate multiple systems onto one embedded platform, streamlining safety certification and reducing project risk.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-helix-hero.jpg', caps:[{title:'Mixed-Criticality Isolation',desc:'Run safety-critical and general-purpose workloads on one SoC'},{title:'Safety Certifiable',desc:'DO-178C DAL A, ISO 26262 ASIL D, IEC 61508 SIL 3'},{title:'Hardware Consolidation',desc:'Reduce SWaP and BOM cost with single-board deployment'},{title:'VxWorks & Linux Guest',desc:'Host certified RTOS and Linux guests simultaneously'}], cert:'DO-178C DAL A / ISO 26262 ASIL D / IEC 61508 SIL 3', wrUrl:'/products/virtualization/helix-virtualization-platform' },
    { id:'linux', name:'Wind River Linux', badge:'Embedded Linux', tagline:'임베디드 시스템을 위한 목적 특화 상용 Linux', desc:'Build Yocto Project-based embedded Linux with the benefits of 10+ years of support, proactive CVE protection, and zero per-device fees.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-linux-hero.jpg', caps:[{title:'Yocto Project-Based',desc:'Industry-standard build system with long-term stability'},{title:'Proactive CVE Management',desc:'Security patches delivered before vulnerabilities become threats'},{title:'10+ Year Lifecycle',desc:'Extended support beyond upstream community lifetimes'},{title:'Zero Per-Device Fees',desc:'Cost-effective scaling from prototype to mass production'}], cert:'', wrUrl:'/products/linux/wind-river-linux' },
    { id:'zephyr', name:'Zephyr', badge:'RTOS Services', tagline:'Zephyr RTOS를 위한 Wind River 전문 라이프사이클 서비스', desc:"Innovate faster, stay ahead of what's next. Wind River is your Zephyr Project partner for real-time embedded use cases.", heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-zephyr-hero.jpg', caps:[{title:'Commercial Support',desc:'Professional-grade support for the Zephyr RTOS ecosystem'},{title:'Security Hardening',desc:'Proactive CVE monitoring and patch delivery for Zephyr'},{title:'Extended Lifecycle',desc:'Long-term maintenance beyond community release cadence'},{title:'Expert Engineering',desc:'Wind River engineers contributing to and supporting Zephyr'}], cert:'', wrUrl:'/products/zephyr' },
  ],
  'Studio Tools': [
    { id:'workspace', name:'Workspace', badge:'Studio Tools — Develop', tagline:'클라우드 기반 임베디드 개발 환경 표준화', desc:'Workspace standardizes embedded software development with secure, preconfigured cloud environments for collaborative coding.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Preconfigured Environments',desc:'Instant dev environments with all tools pre-installed'},{title:'Secure Collaboration',desc:'Role-based access and audit trails for team workflows'},{title:'IDE Flexibility',desc:'VS Code-compatible in cloud or on-premises deployment'},{title:'CI/CD Integration',desc:'Seamless connection to popular DevOps toolchains'}], cert:'', wrUrl:'/products/studio-tools/workspace' },
    { id:'diab-compiler', name:'Diab Compiler', badge:'Studio Tools — Develop', tagline:'안전 임계 임베디드 시스템용 안전 인증 컴파일러', desc:'Build deterministic, safety-certifiable code for mission-critical systems with certified C/C++ runtime libraries.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Safety Certifiable',desc:'TÜV Certified for DO-178C, IEC 61508, and ISO 26262'},{title:'Optimized Code',desc:'Superior performance on Arm, PowerPC, RISC-V, and x86'},{title:'Standards Compliance',desc:'Full C11/C++17 standards support with MISRA checking'},{title:'Long Lifecycle',desc:'Compiler stability across multi-decade program lifetimes'}], cert:'TÜV Certified · DO-178C · IEC 61508 · ISO 26262', wrUrl:'/products/studio-tools/diab-compiler' },
    { id:'pipeline-manager', name:'Pipeline Manager', badge:'Studio Tools — Develop', tagline:'임베디드 SW CI/CD 파이프라인 오케스트레이션', desc:'Pipeline Manager orchestrates pipelines to streamline workflows, automate quality checks, and integrate with existing tools.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Automated Pipelines',desc:'Build, test, and release automation for embedded SW teams'},{title:'Tool Integration',desc:'Connects with Jira, Jenkins, GitLab, and more'},{title:'Compliance Traceability',desc:'Artifact traceability for DO-178C and IEC 62443 audits'},{title:'Scalable Execution',desc:'Distributed agent pools for parallel pipeline execution'}], cert:'', wrUrl:'/products/studio-tools/pipeline-manager' },
    { id:'test-automation', name:'Test Automation', badge:'Studio Tools — Test', tagline:'임베디드 소프트웨어 클라우드 규모 테스트 자동화', desc:'Test Automation speeds testing, verification, and validation of embedded systems using secure cloud-hosted resources.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Cloud-Scale Testing',desc:'Massively parallel test execution in the cloud'},{title:'Hardware-in-the-Loop',desc:'Connect real hardware targets through Virtual Lab integration'},{title:'Coverage Analysis',desc:'Code and requirements coverage reporting for certification'},{title:'Regression Automation',desc:'Continuous regression suites with automatic failure triage'}], cert:'', wrUrl:'/products/studio-tools/test-automation' },
    { id:'virtual-lab', name:'Virtual Lab', badge:'Studio Tools — Test', tagline:'임베디드 테스트 하드웨어 병목 해소', desc:'Virtual Lab connects distributed teams to physical and simulated targets through secure, cloud-native orchestration.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Remote Target Access',desc:'Secure browser-based access to physical and simulated HW'},{title:'Resource Scheduling',desc:'Queue and share scarce hardware resources across teams'},{title:'Simics Integration',desc:'Seamless simulation when physical targets are unavailable'},{title:'Lab Automation',desc:'Script-driven board power, flash, and reset operations'}], cert:'', wrUrl:'/products/studio-tools/virtual-lab' },
    { id:'simics', name:'Intel® Simics®', badge:'Studio Tools — Test', tagline:'복합 전자 시스템 전체 시뮬레이션', desc:"Intel® Simics® decouples software developers' work from hardware availability, enabling true DevSecOps practices to deliver better software faster.", heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Full-System Simulation',desc:'Simulate complete SoCs, boards, and multi-node networks'},{title:'Deterministic Replay',desc:'Record and replay exact execution for reliable debugging'},{title:'Pre-Silicon Development',desc:'Start software development before hardware is available'},{title:'Security Testing',desc:'Inject faults and test security responses safely in simulation'}], cert:'', wrUrl:'/products/studio-tools/simics' },
    { id:'ota-updates', name:'OTA Updates', badge:'Studio Tools — Manage', tagline:'분산 엣지 디바이스 대규모 보안 OTA 업데이트', desc:'Over-the-Air Updates provide secure orchestration and automation for multi-device software updates through the cloud.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Secure Delivery',desc:'Signed and encrypted update packages with rollback protection'},{title:'Fleet Management',desc:'Manage updates across thousands of distributed devices'},{title:'Staged Rollouts',desc:'Canary and phased rollout strategies to manage risk'},{title:'Compliance Auditing',desc:'Full audit trail for regulatory and certification requirements'}], cert:'', wrUrl:'/products/studio-tools/ota-updates' },
    { id:'conductor', name:'Conductor', badge:'Studio Tools — Manage', tagline:'엣지-코어 대규모 분산 클라우드 운영 자동화', desc:'Simplify distributed cloud operations with unified orchestration and centralized control — for any deployment size.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Unified Control Plane',desc:'Centralized management for edge, cloud, and hybrid deployments'},{title:'GitOps Workflows',desc:'Declarative configuration management with Git-based workflows'},{title:'Multi-Cluster Support',desc:'Orchestrate workloads across many Kubernetes clusters'},{title:'Policy Enforcement',desc:'Automated compliance policies across the entire fleet'}], cert:'', wrUrl:'/products/studio-tools/conductor' },
    { id:'digital-feedback-loop', name:'Digital Feedback Loop', badge:'Studio Tools — Monitor', tagline:'실시간 엣지 인사이트로 임베디드 SW 품질 개선', desc:'Digital Feedback Loop provides continuous monitoring and analytics to improve embedded software quality, performance, and compliance.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Runtime Telemetry',desc:'Collect logs, metrics, and traces from deployed devices'},{title:'Anomaly Detection',desc:'AI-driven detection of performance and reliability issues'},{title:'Closed-Loop Feedback',desc:'Connect field data directly to development and test pipelines'},{title:'Compliance Monitoring',desc:'Continuous compliance checks against security baselines'}], cert:'', wrUrl:'/products/studio-tools/digital-feedback-loop' },
    { id:'analytics', name:'Analytics', badge:'Studio Tools — Monitor', tagline:'클라우드 인프라 실시간 데이터 인텔리전스', desc:'Turn Data into Actionable Intelligence — detect issues early and make decisions faster with real-time cloud infrastructure insights.', heroImg:'https://www.windriver.com/sites/default/files/2024-08/2040x800-studio-hero.jpg', caps:[{title:'Real-Time Dashboards',desc:'Live visibility into cloud infrastructure performance'},{title:'Anomaly Detection',desc:'ML-powered alerts before issues impact availability'},{title:'Data Aggregation & Enrichment',desc:'Augment structured and unstructured data with context'},{title:'Data Lifecycle Management',desc:'Replication indexing ensures redundancy and fast search'}], cert:'', wrUrl:'/products/studio-tools/analytics' },
  ],
};

/* vendor 기준으로 cat들을 그룹핑 — editorType은 첫 번째 cat에서 읽음 */
function buildVendors(cats) {
  const map = new Map();
  (cats || []).forEach(cat => {
    const v = cat.vendor?.trim() || '(미지정)';
    if (!map.has(v)) map.set(v, { cats: [], editorType: cat.editorType || 'form' });
    map.get(v).cats.push(cat);
  });
  return Array.from(map.entries()).map(([name, { cats, editorType }]) => ({ name, cats, editorType }));
}

function specsToText(sections) {
  const arr = Array.isArray(sections) ? sections : (sections ? [sections] : []);
  return arr.map(sec => {
    const rows = (sec.specs || []).map(s => `${s.key}: ${s.value}`).join('\n');
    return sec.title ? `${sec.title}\n${rows}` : rows;
  }).join('\n\n');
}


function parseItem(it) {
  if (it._wr) {
    const content = it.sectionContent || it.bnContent || '';
    const initial = (!content && it.id) ? getInitialContent(it.id) : null;
    return {
      _id: uid(), _wr: true,
      model: it.name || it.id || '',
      id: it.id || '', badge: it.badge || '', tagline: it.tagline || '',
      heroImg: it.heroImg || '',
      sectionContent: content || (initial ? JSON.stringify(initial) : ''),
      bnContent: content || (initial ? JSON.stringify(initial) : ''),
      cert: it.cert || '', wrUrl: it.wrUrl || '',
    };
  }
  return {
    _id: uid(),
    model: it.model || '', category: it.category || '',
    imageKeys: Array.isArray(it.imageKeys) ? it.imageKeys : (it.imageKey ? [it.imageKey] : []),
    features: Array.isArray(it.features) ? it.features.join('\n') : (typeof it.features === 'string' ? it.features : ''),
    tags: Array.isArray(it.tags) ? it.tags : [],
    specSheets: it.specSheets
      ? it.specSheets.map(s => ({ _id: uid(), label: s.label || '', content: s.content || '' }))
      : [
          { _id: uid(), label: 'Technical Profile', content: it.profile || '' },
          { _id: uid(), label: 'Specifications', content: specsToText(it.specSections || []) },
          { _id: uid(), label: 'Order Information', content: typeof it.orderInfo === 'string' ? it.orderInfo : '' },
        ],
    links: (it.links || []).map(l => ({ _id: uid(), key: l.key || '', url: l.url || '' })),
  };
}

function parseRow(row) {
  let data = {};
  try { data = JSON.parse(row.textCategory || '{}'); } catch (_) {}
  if (data.subcat !== undefined && data.category === undefined) {
    data = { category: data.subcat || '', subcats: [{ subcat: data.subcat || '', items: data.items || [] }] };
  }
  return {
    intNum: row.intNum, _id: uid(),
    vendor: data.vendor || '',
    editorType: data.editorType || (data.vendor === 'Wind River' ? 'blocknote' : 'form'),
    category: data.category || '',
    subcats: (data.subcats || []).map(sc => ({
      _id: uid(), subcat: sc.subcat || '',
      items: (sc.items || []).map(parseItem),
    })),
  };
}

export default function Military({ refreshKey = 0, onRegisterSave, onRegisterDirty, onBack }) {
  const [cats,      setCats]      = useState(null);
  const [vendorIdx, setVendorIdx] = useState(0);
  const [catIdx,    setCatIdx]    = useState(0);   // active vendor 내 중분류 인덱스
  const [subcatIdx, setSubcatIdx] = useState(0);
  const [itemIdx,   setItemIdx]   = useState(0);
  const [specTabIdx, setSpecTabIdx] = useState(0);
  const ref    = useRef([]);
  const delRef = useRef([]);
  const dirtyRef = useRef(false);
  const filePick = useRef(null);
  const filePending = useRef(null);
  const [fileUploading, setFileUploading] = useState(false);

  const uploadFile = useCallback(async (file) => {
    if (!filePending.current) return;
    const applyUrl = filePending.current;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch('/api/files', { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `업로드 실패 (${res.status})`); }
      const f = await res.json();
      applyUrl(f.url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || '파일 업로드 실패');
    } finally {
      setFileUploading(false);
      filePending.current = null;
    }
  }, []);

  const { data: domainData, setField: setDomainField, saveSection: saveDomainSection, isDirty: domainIsDirty } =
    useSection('product:military', 'domain-header', 0);

  /* stale closure 방지용 ref */
  const activeVendorCatsRef = useRef([]);
  const gciRef = useRef(-1);
  const subcatIdxRef = useRef(0);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?slug=${SLUG}`).then(r => r.json()).then(rows => {
      if (!alive) return;
      const parsed = (rows || []).map(parseRow);
      ref.current = parsed;
      setCats(parsed);
    }).catch(() => { if (alive) setCats([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  useEffect(() => { setCatIdx(0); setSubcatIdx(0); setItemIdx(0); setSpecTabIdx(0); }, [vendorIdx]);
  useEffect(() => { setSubcatIdx(0); setItemIdx(0); setSpecTabIdx(0); }, [catIdx]);
  useEffect(() => { setItemIdx(0); setSpecTabIdx(0); }, [subcatIdx]);
  useEffect(() => { setSpecTabIdx(0); }, [itemIdx]);

  const push = (next) => { dirtyRef.current = true; ref.current = next; setCats([...next]); };

  /* ── 파생값 ── */
  const vendors          = buildVendors(cats);
  const activeVendorCats = vendors[vendorIdx]?.cats ?? [];
  const activeCat        = activeVendorCats[catIdx];
  const gci              = cats ? cats.findIndex(c => c._id === activeCat?._id) : -1;

  activeVendorCatsRef.current = activeVendorCats;
  gciRef.current = gci;
  subcatIdxRef.current = subcatIdx;

  /* ── 대분류(vendor) ── */
  const addVendorGroup = () => {
    const name = window.prompt('새 제조사 이름을 입력하세요:');
    if (!name?.trim()) return;
    const n = newCat(name.trim());
    const next = [...ref.current, n];
    push(next);
    const newVendors = buildVendors(next);
    const vi = newVendors.findIndex(v => v.name === name.trim());
    setVendorIdx(vi >= 0 ? vi : newVendors.length - 1);
    setCatIdx(0);
  };

  /* ── 중분류(category) ── */
  const addCat = () => {
    const vName = vendors[vendorIdx]?.name ?? '';
    const n = newCat(vName);
    const next = [...ref.current, n];
    push(next);
    const newLen = buildVendors(next)[vendorIdx]?.cats.length ?? 1;
    setCatIdx(newLen - 1);
  };

  const delCat = () => {
    if (!activeCat) return;
    if (activeCat.intNum && !window.confirm('이 중분류를 삭제할까요?')) return;
    if (activeCat.intNum) delRef.current.push(activeCat.intNum);
    const next = ref.current.filter(c => c._id !== activeCat._id);
    push(next);
    const newLen = buildVendors(next)[vendorIdx]?.cats.length ?? 0;
    setCatIdx(ci => Math.max(0, Math.min(ci, newLen - 1)));
  };

  const updCat = (val) => {
    if (gci < 0) return;
    push(ref.current.map((c, i) => i === gci ? { ...c, category: val } : c));
  };

  /* vendor-local 인덱스를 global 인덱스로 변환해 순서 변경 */
  const reorderCat = useCallback((from, to) => {
    const vcats = activeVendorCatsRef.current;
    const fromId = vcats[from]?._id;
    const toId   = vcats[to]?._id;
    if (!fromId || !toId) return;
    const fi = ref.current.findIndex(c => c._id === fromId);
    const ti = ref.current.findIndex(c => c._id === toId);
    if (fi < 0 || ti < 0) return;
    const next = [...ref.current];
    const [m] = next.splice(fi, 1);
    next.splice(ti, 0, m);
    push(next);
    setCatIdx(to);
  }, []); // eslint-disable-line

  /* ── 소분류 ── */
  const addSubcat = () => {
    const ci = gciRef.current;
    if (ci < 0) return;
    const next = ref.current.map((c, i) => i === ci ? { ...c, subcats: [...c.subcats, newSubcat()] } : c);
    push(next);
    setSubcatIdx(ref.current[ci].subcats.length);
  };
  const delSubcat = (si) => {
    const ci = gciRef.current;
    if (ci < 0) return;
    push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.filter((_, j) => j !== si) } : c));
    setSubcatIdx(idx => Math.max(0, Math.min(idx, ref.current[ci].subcats.length - 2)));
  };
  const updSubcat = (si, val) => {
    const ci = gciRef.current;
    if (ci < 0) return;
    push(ref.current.map((c, i) =>
      i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, subcat: val } : sc) } : c));
  };
  const reorderSubcat = useCallback((from, to) => {
    const ci = gciRef.current;
    if (ci < 0) return;
    const next = ref.current.map((c, i) => {
      if (i !== ci) return c;
      const subs = [...c.subcats]; const [m] = subs.splice(from, 1); subs.splice(to, 0, m);
      return { ...c, subcats: subs };
    });
    push(next); setSubcatIdx(to);
  }, []); // eslint-disable-line

  const reorderItem = useCallback((from, to) => {
    const ci = gciRef.current;
    const si = subcatIdxRef.current;
    if (ci < 0) return;
    const next = ref.current.map((c, i) => {
      if (i !== ci) return c;
      return { ...c, subcats: c.subcats.map((sc, j) => {
        if (j !== si) return sc;
        const its = [...sc.items]; const [m] = its.splice(from, 1); its.splice(to, 0, m);
        return { ...sc, items: its };
      })};
    });
    push(next); setItemIdx(to);
  }, []); // eslint-disable-line

  /* ── 제품 ── */
  const addItem = (si) => {
    const ci = gciRef.current;
    if (ci < 0) return;
    const isWr = vendors[vendorIdx]?.editorType === 'blocknote';
    const newIt = isWr ? newWrItem() : newItem();
    const next = ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
      j === si ? { ...sc, items: [...sc.items, newIt] } : sc) } : c);
    push(next); setItemIdx(ref.current[ci].subcats[si].items.length);
  };
  const delItem = (si, ii) => {
    const ci = gciRef.current;
    if (ci < 0) return;
    push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
      j === si ? { ...sc, items: sc.items.filter((_, k) => k !== ii) } : sc) } : c));
    setItemIdx(idx => Math.max(0, Math.min(idx, ref.current[ci].subcats[si].items.length - 2)));
  };
  const updItem = (si, ii, field, val) => {
    const ci = gciRef.current;
    if (ci < 0) return;
    push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
      j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, [field]: val } : it) } : sc) } : c));
  };

  /* ── 이미지 ── */
  const setImageKey   = (si, ii, imgIdx, key) => { const ci = gciRef.current; if (ci < 0) return; push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, items: sc.items.map((it, k) => { if (k !== ii) return it; const keys = [...it.imageKeys]; keys[imgIdx] = key; return { ...it, imageKeys: keys }; }) } : sc) } : c)); };
  const addImageSlot  = (si, ii) => { const ci = gciRef.current; if (ci < 0) return; push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, imageKeys: [...it.imageKeys, ''] } : it) } : sc) } : c)); };
  const delImageSlot  = (si, ii, imgIdx) => { const ci = gciRef.current; if (ci < 0) return; push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, imageKeys: it.imageKeys.filter((_, p) => p !== imgIdx) } : it) } : sc) } : c)); };

  /* ── 스펙시트 ── */
  const addSpecSheet = () => {
    const si = subcatIdx, ii = itemIdx;
    const ci = gciRef.current;
    if (ci < 0) return;
    const cur = ref.current[ci]?.subcats[si]?.items[ii]?.specSheets ?? [];
    push(ref.current.map((c, i) => i !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) => j !== si ? sc : {
      ...sc, items: sc.items.map((it, k) => k !== ii ? it : { ...it, specSheets: [...it.specSheets, newSpecSheet()] })
    })}));
    setSpecTabIdx(cur.length);
  };
  const delSpecSheet = (pi) => {
    const si = subcatIdx, ii = itemIdx;
    const ci = gciRef.current;
    if (ci < 0) return;
    push(ref.current.map((c, i) => i !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) => j !== si ? sc : {
      ...sc, items: sc.items.map((it, k) => k !== ii ? it : { ...it, specSheets: it.specSheets.filter((_, p) => p !== pi) })
    })}));
    setSpecTabIdx(t => Math.max(0, t - (pi <= t ? 1 : 0)));
  };
  const updSpecSheet = (pi, field, val) => {
    const si = subcatIdx, ii = itemIdx;
    const ci = gciRef.current;
    if (ci < 0) return;
    push(ref.current.map((c, i) => i !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) => j !== si ? sc : {
      ...sc, items: sc.items.map((it, k) => k !== ii ? it : { ...it, specSheets: it.specSheets.map((sh, p) => p !== pi ? sh : { ...sh, [field]: val }) })
    })}));
  };

  /* ── 링크 ── */
  const addLink = (si, ii) => { const ci = gciRef.current; if (ci < 0) return; push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: [...it.links, newLink()] } : it) } : sc) } : c)); };
  const delLink = (si, ii, li) => { const ci = gciRef.current; if (ci < 0) return; push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: it.links.filter((_, p) => p !== li) } : it) } : sc) } : c)); };
  const updLink = (si, ii, li, field, val) => { const ci = gciRef.current; if (ci < 0) return; push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: it.links.map((l, p) => p === li ? { ...l, [field]: val } : l) } : it) } : sc) } : c)); };


  /* ── 저장 ── */
  const save = useCallback(async () => {
    for (const id of delRef.current) {
      const r = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`삭제 실패 (${r.status})`);
    }
    delRef.current = [];
    for (let i = 0; i < ref.current.length; i++) {
      const cat = ref.current[i];
      const textCategory = JSON.stringify({
        vendor: cat.vendor,
        editorType: cat.editorType || 'form',
        category: cat.category,
        subcats: cat.subcats.map(sc => ({
          subcat: sc.subcat,
          items: sc.items.map(it => {
            if (it._wr) return {
              _wr: true, id: it.id, name: it.model,
              badge: it.badge, tagline: it.tagline,
              heroImg: it.heroImg, sectionContent: it.sectionContent || it.bnContent || '', bnContent: it.sectionContent || it.bnContent || '',
              cert: it.cert, wrUrl: it.wrUrl,
            };
            return {
              model: it.model, category: it.category,
              slug: toSlug(it.model),
              imageKeys: it.imageKeys.filter(Boolean),
              features: it.features.split('\n').map(s => s.trim()).filter(Boolean),
              tags: it.tags || [],
              specSheets: it.specSheets.map(s => ({ label: s.label, content: s.content })),
              links: it.links.map(l => ({ key: l.key, url: l.url })),
            };
          }),
        })),
      });
      const r = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intNum: cat.intNum || undefined, strSlug: SLUG, textCategory, sort: i }),
      });
      if (!r.ok) throw new Error(`저장 실패 (${r.status})`);
      const saved = await r.json();
      if (!cat.intNum) ref.current = ref.current.map(x => x === cat ? { ...x, intNum: saved.intNum } : x);
    }
    await saveDomainSection();
    dirtyRef.current = false;
  }, [saveDomainSection]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);
  const isDirty = useCallback(() => dirtyRef.current || domainIsDirty(), [domainIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  const { containerRef: catTabsRef,    dragProps: catDragProps,    dropProps: catDropProps,    cardClass: catCardClass    } = useDragReorder(reorderCat);
  const { containerRef: subcatTabsRef, dragProps: subcatDragProps, dropProps: subcatDropProps, cardClass: subcatCardClass } = useDragReorder(reorderSubcat);
  const { containerRef: itemBarRef,    dragProps: itemDragProps,   dropProps: itemDropProps,   cardClass: itemCardClass   } = useDragReorder(reorderItem);

  /* ── Wind River 기본값 시드 ── */
  const seedWr = () => {
    if (!window.confirm('Wind River 기본값(16개)을 불러옵니다. 현재 Wind River 데이터가 초기화됩니다. 계속하시겠습니까?')) return;
    const withoutWr = ref.current.filter(c => c.vendor !== 'Wind River');
    const wrCats = Object.entries(WR_DEFAULTS).map(([category, products]) => ({
      _id: uid(), intNum: null, vendor: 'Wind River', category,
      subcats: products.map(p => ({
        _id: uid(), subcat: p.name,
        items: [{ _id: uid(), _wr: true, model: p.name, ...p }],
      })),
    }));
    const next = [...withoutWr, ...wrCats];
    push(next);
    const newVendors = buildVendors(next);
    const wi = newVendors.findIndex(v => v.name === 'Wind River');
    if (wi >= 0) setVendorIdx(wi);
    setCatIdx(0); setSubcatIdx(0); setItemIdx(0);
  };

  if (cats === null) return <div className="rp-admin-wrap"><p style={{ color: '#888' }}>불러오는 중…</p></div>;

  const isBlocknote  = vendors[vendorIdx]?.editorType === 'blocknote';
  const isWrVendor   = isBlocknote; // backward compat alias
  const subcats      = activeCat?.subcats ?? [];
  const activeSubcat = subcats[subcatIdx];
  const items        = activeSubcat?.items ?? [];
  const it           = items[itemIdx] ?? items[0];

  return (
    <div className="rp-admin-wrap">
      <h2 className="rp-admin-title">Military &amp; Aerospace</h2>

      {/* ── 도메인 헤더 (홈페이지 제품 목록 위 콘텐츠) ── */}
      <div style={{ marginBottom: 28, paddingBottom: 28, borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>도메인 헤더</span>
          <span style={{ fontSize: 12, color: '#888' }}>홈페이지 제품 목록 위에 표시되는 콘텐츠 (적용 분야, 소개 등)</span>
        </div>
        <SectionBuilder value={domainData.content || ''} onChange={(v) => setDomainField('content', v)} />
      </div>

      {/* ── 대분류 탭 (vendor/제조사) ── */}
      <div className="rp-admin-vendor-tabrow">
        <ul className="rp-admin-vendor-tabs">
          {vendors.map((v, vi) => (
            <li key={v.name} className={vi === vendorIdx ? 'active' : ''}>
              <button type="button" onClick={() => setVendorIdx(vi)}>{v.name}</button>
            </li>
          ))}
        </ul>
        <button type="button" className="rp-admin-add-vendor" onClick={addVendorGroup}>+ 대분류</button>
        {isWrVendor && (
          <button type="button" className="rp-admin-add-vendor" style={{ background: '#1e3a5f', color: '#fff', border: 'none', marginLeft: 8 }} onClick={seedWr}>
            기본값 불러오기 (16개)
          </button>
        )}
      </div>

      {/* ── 중분류 탭 (category, active vendor 내) ── */}
      <div className="rp-admin-tabrow">
        <ul className="rp-admin-tabs" ref={catTabsRef}>
          {activeVendorCats.map((cat, ci) => (
            <li key={cat._id} data-flip={cat._id}
              className={`${ci === catIdx ? 'active' : ''} ${catCardClass(ci)}`}
              {...catDragProps(ci)} {...catDropProps(ci)}>
              <span className="rp-admin-tab-handle">⠿</span>
              <input className="rp-admin-tab-inp" value={cat.category} placeholder="중분류명"
                onClick={() => setCatIdx(ci)} onChange={(e) => updCat(e.target.value)} />
              <button type="button" className="rp-admin-tab-del" onClick={delCat} title="중분류 삭제">×</button>
            </li>
          ))}
        </ul>
        <button type="button" className="rp-admin-add-tab" onClick={isWrVendor
          ? () => { const n = newWrCat('Wind River', '새 중분류'); const next = [...ref.current, n]; push(next); const newLen = buildVendors(next)[vendorIdx]?.cats.length ?? 1; setCatIdx(newLen - 1); }
          : addCat}>+ 중분류</button>
      </div>

      {!activeCat ? (
        <div className="rp-admin-empty-state"><p>중분류를 먼저 추가하세요.</p></div>
      ) : (
      <>
      {/* ── 소분류 탭 ── */}
      <div className="rp-admin-subcatrow">
        <ul className="rp-admin-subcattabs" ref={subcatTabsRef}>
          {subcats.map((sc, si) => (
            <li key={sc._id} data-flip={sc._id}
              className={`${si === subcatIdx ? 'active' : ''} ${subcatCardClass(si)}`}
              {...subcatDragProps(si)} {...subcatDropProps(si)}>
              <span className="rp-admin-tab-handle" style={{ fontSize: '0.8rem', padding: '6px 3px 6px 6px' }}>⠿</span>
              <input className="rp-admin-subcattab-inp" value={sc.subcat} placeholder="소분류명"
                onClick={() => setSubcatIdx(si)} onChange={(e) => updSubcat(si, e.target.value)} />
              <button type="button" className="rp-admin-tab-del" onClick={() => delSubcat(si)}>×</button>
            </li>
          ))}
        </ul>
        <button type="button" className="rp-admin-add-subcattab"
          onClick={() => isWrVendor ? (() => { const n = newWrSubcat(); const ci = gciRef.current; if (ci < 0) return; const next = ref.current.map((c, i) => i === ci ? { ...c, subcats: [...c.subcats, n] } : c); push(next); setSubcatIdx(ref.current[ci].subcats.length); })() : addSubcat()}>
          + {isWrVendor ? '제품' : '소분류'}
        </button>
      </div>

      {!activeSubcat ? (
        <div className="rp-admin-empty-state"><p>{isWrVendor ? '제품을 추가하세요.' : '소분류를 먼저 추가하세요.'}</p></div>
      ) : (
      <>
      {/* ── 제품 탭 바 (일반 하드웨어만) ── */}
      {!isWrVendor && (
        <div className="rp-admin-item-bar" ref={itemBarRef}>
          {items.map((item, i) => (
            <div key={item._id} data-flip={item._id}
              className={`rp-admin-item-tab ${i === itemIdx ? 'active' : ''} ${itemCardClass(i)}`}
              {...itemDragProps(i)} {...itemDropProps(i)}>
              <span className="rp-admin-tab-handle">⠿</span>
              <button type="button" onClick={() => setItemIdx(i)}>
                {item.model || `제품 ${i + 1}`}
              </button>
            </div>
          ))}
          <button type="button" className="rp-admin-item-add" onClick={() => addItem(subcatIdx)}>+ 제품 추가</button>
        </div>
      )}

      {it ? (
        isBlocknote ? (
        /* ── Wind River 소프트웨어 에디터 ── */
        <div className="rp-admin-editor">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button type="button" className="rp-admin-item-del" onClick={() => delSubcat(subcatIdx)}>제품 삭제 ×</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <label className="rp-admin-field-label">ID (slug)</label>
              <input className="rp-admin-field-inp" value={it.id || ''} placeholder="vxworks"
                onChange={(e) => updItem(subcatIdx, 0, 'id', e.target.value)} />
            </div>
            <div>
              <label className="rp-admin-field-label">뱃지</label>
              <input className="rp-admin-field-inp" value={it.badge || ''} placeholder="RTOS"
                onChange={(e) => updItem(subcatIdx, 0, 'badge', e.target.value)} />
            </div>
            <div>
              <label className="rp-admin-field-label">제품명</label>
              <input className="rp-admin-field-inp" value={it.model || ''} placeholder="VxWorks"
                onChange={(e) => { updItem(subcatIdx, 0, 'model', e.target.value); updSubcat(subcatIdx, e.target.value); }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div>
              <label className="rp-admin-field-label">한국어 태그라인</label>
              <input className="rp-admin-field-inp" value={it.tagline || ''} placeholder="세계 1위 실시간 운영체제"
                onChange={(e) => updItem(subcatIdx, 0, 'tagline', e.target.value)} />
            </div>
            <div>
              <label className="rp-admin-field-label">인증 (cert)</label>
              <input className="rp-admin-field-inp" value={it.cert || ''} placeholder="DO-178C / IEC 61508"
                onChange={(e) => updItem(subcatIdx, 0, 'cert', e.target.value)} />
            </div>
            <div>
              <label className="rp-admin-field-label">Wind River URL (경로)</label>
              <input className="rp-admin-field-inp" value={it.wrUrl || ''} placeholder="/products/embedded/vxworks"
                onChange={(e) => updItem(subcatIdx, 0, 'wrUrl', e.target.value)} />
            </div>
          </div>

          {/* 섹션 편집기 */}
          <SectionBuilder
            key={it._id}
            value={it.sectionContent || it.bnContent || ''}
            onChange={(json) => {
              updItem(subcatIdx, 0, 'sectionContent', json);
              updItem(subcatIdx, 0, 'bnContent', json);
            }}
          />
        </div>
        ) : (
        /* ── 일반 하드웨어 제품 에디터 ── */
        <div className="rp-admin-editor">
          <div className="rp-admin-top">
            <div className="rp-admin-gallery">
              <div className="rp-admin-gallery-label">
                이미지 갤러리 ({it.imageKeys.length}/6)
                {it.imageKeys.length < 6 && (
                  <button type="button" className="rp-admin-gallery-add" onClick={() => addImageSlot(subcatIdx, itemIdx)}>+ 추가</button>
                )}
              </div>
              <div className="rp-admin-gallery-grid">
                {it.imageKeys.map((key, imgIdx) => (
                  <div key={key || `slot-${imgIdx}`} className="rp-admin-gallery-slot">
                    <button type="button" className="rp-admin-gallery-del" onClick={() => delImageSlot(subcatIdx, itemIdx, imgIdx)}>×</button>
                    <ImageDrop value={key} onChange={(k) => setImageKey(subcatIdx, itemIdx, imgIdx, k)} />
                  </div>
                ))}
                {it.imageKeys.length === 0 && (
                  <div className="rp-admin-gallery-empty" onClick={() => addImageSlot(subcatIdx, itemIdx)}>
                    <span>+ 이미지 추가</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rp-admin-info">
              <button type="button" className="rp-admin-item-del" onClick={() => delItem(subcatIdx, itemIdx)}>제품 삭제 ×</button>
              <label className="rp-admin-field-label">모델명</label>
              <input className="rp-admin-field-inp" value={it.model} placeholder="SBC3518"
                onChange={(e) => updItem(subcatIdx, itemIdx, 'model', e.target.value)} />
              <label className="rp-admin-field-label">카테고리 / 폼팩터</label>
              <input className="rp-admin-field-inp" value={it.category} placeholder="3U VPX Single Board Computer"
                onChange={(e) => updItem(subcatIdx, itemIdx, 'category', e.target.value)} />
              <label className="rp-admin-field-label">주요 특징 (한 줄에 하나씩)</label>
              <textarea className="rp-admin-profile-inp" value={it.features} rows={6}
                placeholder={"Intel® Core™ i7 Processor\nSOSA Aligned\nRugged 3U VPX\n..."}
                onChange={(e) => updItem(subcatIdx, itemIdx, 'features', e.target.value)} />
              <label className="rp-admin-field-label">태그 (필터·검색용, Enter로 추가)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {(it.tags || []).map((tag, ti) => (
                  <span key={ti} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: '#e0e7ff', borderRadius: 12, fontSize: 13 }}>
                    {tag}
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#6366f1' }}
                      onClick={() => updItem(subcatIdx, itemIdx, 'tags', (it.tags || []).filter((_, i) => i !== ti))}>×</button>
                  </span>
                ))}
              </div>
              <input className="rp-admin-field-inp" placeholder="태그 입력 후 Enter"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const v = e.target.value.trim();
                    if (!(it.tags || []).includes(v)) updItem(subcatIdx, itemIdx, 'tags', [...(it.tags || []), v]);
                    e.target.value = '';
                  }
                }} />
              <label className="rp-admin-field-label">링크 버튼 (최대 3개)</label>
              {it.links.map((lnk, li) => (
                <div key={lnk._id} className="rp-admin-link-row">
                  <input className="rp-admin-link-key" value={lnk.key} placeholder="Information"
                    onChange={(e) => updLink(subcatIdx, itemIdx, li, 'key', e.target.value)} />
                  <input className="rp-admin-link-url" value={lnk.url} placeholder="https://..."
                    onChange={(e) => updLink(subcatIdx, itemIdx, li, 'url', e.target.value)} />
                  <button type="button" className="rp-admin-spec-del" onClick={() => delLink(subcatIdx, itemIdx, li)}>×</button>
                </div>
              ))}
              {it.links.length < 3 && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="rp-admin-add-btn" onClick={() => addLink(subcatIdx, itemIdx)}>+ 링크 추가</button>
                  <button type="button" className="rp-admin-add-btn" disabled={fileUploading}
                    onClick={() => {
                      filePending.current = (url) => {
                        const ci = gciRef.current;
                        if (ci < 0) return;
                        push(ref.current.map((c, ci2) => ci2 !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) =>
                          j !== subcatIdx ? sc : { ...sc, items: sc.items.map((it2, k) =>
                            k !== itemIdx ? it2 : { ...it2, links: [...it2.links, { _id: uid(), key: 'Information', url }] }
                          )}
                        )}));
                      };
                      filePick.current?.click();
                    }}>
                    {fileUploading ? '업로드 중…' : '+ 파일 추가'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── 스펙시트 탭 ── */}
          <div className="rp-admin-detail-tabs">
            {(it.specSheets || []).map((sheet, pi) => (
              <div key={sheet._id} className="rp-admin-tab-wrap">
                <button type="button"
                  className={`rp-admin-detail-tab-btn${specTabIdx === pi ? ' active' : ''}`}
                  onClick={() => setSpecTabIdx(pi)}>
                  {sheet.label}
                </button>
                {(it.specSheets || []).length > 1 && (
                  <button type="button" className="rp-admin-tab-del-btn"
                    onClick={() => delSpecSheet(pi)}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="rp-admin-tab-add-btn" onClick={addSpecSheet}>+ 탭</button>
          </div>

          {it.specSheets?.[specTabIdx] && (
            <div className="rp-admin-section">
              <input className="rp-admin-field-inp rp-admin-sheet-label-inp"
                value={it.specSheets[specTabIdx].label}
                onChange={(e) => updSpecSheet(specTabIdx, 'label', e.target.value)}
                placeholder="탭 이름" />
              <SectionBuilder
                key={it.specSheets[specTabIdx]._id}
                value={it.specSheets[specTabIdx].content}
                onChange={(json) => updSpecSheet(specTabIdx, 'content', json)}
              />
            </div>
          )}
        </div>
        )
      ) : (
        <div className="rp-admin-empty-state">
          <p>이 소분류에 제품이 없습니다.</p>
          <button type="button" className="rp-admin-add-btn" onClick={() => addItem(subcatIdx)}>+ 제품 추가</button>
        </div>
      )}
      </>
      )}
      </>
      )}

      {onBack && (
        <div style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn-secondary" onClick={onBack}>← 응용 분야로 돌아가기</button>
        </div>
      )}

      <input ref={filePick} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.zip" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
    </div>
  );
}
